import { useEffect, useMemo, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { QueryClientProvider } from 'react-query';
import Select from 'react-select';
import { forms } from '@birdi/theme';
import { stroot } from '@birdi/js-sdk';
import { mapSitesAPI } from '@birdi/js-sdk/src/public/map-sites';
import {
  invalidateSiteForMap,
  invalidateSites,
  mapSitesQueryClient,
  useSiteForMap,
  useSitesList,
} from '../map-sites/queries';
import { ModalShell } from '../map-sites/site-modals';
import {
  cancelButtonCSS,
  confirmFooterCSS,
  primaryButtonCSS,
  selectTheme,
} from '../map-sites/styles';
import { readAPIErrorMessage } from '../map-sites/utils';

const labelCSS = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const AddToSiteForm = ({
  setOpen,
  mapId,
  mapName,
}: {
  setOpen: (value: boolean) => void;
  mapId: string;
  mapName: string;
}) => {
  const theme = useTheme();
  const { data: sites } = useSitesList();
  // The map's existing site, if any — the maps list doesn't carry it, so it's
  // fetched when the modal opens.
  const { data: currentSite, isLoading: loadingCurrentSite } = useSiteForMap(
    mapId,
    true,
  );
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-select the site the map is already in. Runs once the lookup resolves
  // rather than as initial state, since the modal renders before it lands.
  useEffect(() => {
    if (currentSite) setSelectedSiteId(currentSite.id);
  }, [currentSite]);

  const options = useMemo(() => {
    const fromList = (sites ?? []).map((site) => ({
      value: site.id,
      label: site.name,
    }));
    // Keep the pre-selected site selectable even if it isn't in the workspace
    // list (so the dropdown can never show a blank current value).
    if (currentSite && !fromList.some((o) => o.value === currentSite.id)) {
      return [{ value: currentSite.id, label: currentSite.name }, ...fromList];
    }
    return fromList;
  }, [sites, currentSite]);

  const submit = async () => {
    if (!selectedSiteId) return;
    setSubmitting(true);
    setError(null);
    try {
      // Reassigns if the map already belongs to another site — same API the
      // site page's "Change site" uses.
      await mapSitesAPI.addMapToSite(stroot('mt2sit'), selectedSiteId, mapId);
      invalidateSites(selectedSiteId);
      // The site it came from loses a map, so refresh that one too.
      if (currentSite) invalidateSites(currentSite.id);
      invalidateSiteForMap(mapId);
      setOpen(false);
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Nothing to save while the selection is still the site the map is in.
  const unchanged = !!currentSite && selectedSiteId === currentSite.id;

  return (
    <>
      <p
        css={css`
          margin-top: 0;
        `}
      >
        {currentSite ? (
          <>
            <strong>{mapName || 'Untitled Map'}</strong> is currently in{' '}
            <strong>{currentSite.name}</strong>. Choose another site to move it.
          </>
        ) : (
          <>
            Add <strong>{mapName || 'Untitled Map'}</strong> to a site.
          </>
        )}
      </p>
      <label htmlFor="add-to-site-select" css={labelCSS}>
        Site
        <Select
          inputId="add-to-site-select"
          instanceId="add-to-site-select"
          options={options}
          value={options.find((o) => o.value === selectedSiteId) ?? null}
          onChange={(opt: any) => setSelectedSiteId(opt?.value ?? null)}
          placeholder="Select a site..."
          noOptionsMessage={() => 'No sites yet'}
          isLoading={loadingCurrentSite}
          isDisabled={loadingCurrentSite}
          theme={selectTheme(theme)}
          menuPortalTarget={
            typeof window !== 'undefined' ? window.document.body : undefined
          }
          styles={{
            menuPortal: (base: any) => ({
              ...base,
              zIndex: 9999,
              pointerEvents: 'auto',
            }),
          }}
        />
      </label>
      {error && <div css={forms.errorMessage}>{error}</div>}
      <footer css={confirmFooterCSS}>
        <button
          type="button"
          css={cancelButtonCSS}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          css={primaryButtonCSS}
          disabled={!selectedSiteId || unchanged || submitting}
          data-testid="add-to-site-confirm"
          onClick={submit}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </footer>
    </>
  );
};

export interface AddToSiteModalProps {
  open: boolean;
  onChange: (value: boolean) => void;
  mapId: string;
  mapName: string;
}

/**
 * Assign a map to a site from the map tile / table context menu. Same shell,
 * select and footer as the site page's "Change site" modal, worded for adding
 * a map from the map browser. Shares the Map Sites query client so the sites
 * list is cached with the sidebar and the cache is invalidated on save.
 */
export const AddToSiteModal = ({
  open,
  onChange,
  mapId,
  mapName,
}: AddToSiteModalProps) => (
  <QueryClientProvider client={mapSitesQueryClient}>
    <ModalShell title="Add to site" open={open} setOpen={onChange}>
      {/* Mounted only while open so the current-site lookup re-runs each time
          and the pre-selected value can't go stale. */}
      {open && (
        <AddToSiteForm setOpen={onChange} mapId={mapId} mapName={mapName} />
      )}
    </ModalShell>
  </QueryClientProvider>
);
